import { useEffect, useRef, useState } from 'react';

interface PayPalSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  chatType?: 'astrology' | 'dream' | 'four-pillars' | 'destiny';
}

const PAYPAL_CLIENT_ID = 'ASqxzPDJqP6t1EmR8Vth_Vzs1AE2eKm8XOhQkVS7MrZCieWn38JTmLhCo8za87s9r3tHlDK34aIyeQzF';
const PAYPAL_PLAN_ID = 'P-6SM613877Y558645CNHCYDOQ';
const PAYPAL_CONTAINER_ID = 'paypal-sub-btn-container';

const CHAT_TYPE_INFO: Record<string, { icon: string; label: string; color: string }> = {
  astrology: { icon: 'ri-star-line', label: '점성술 무제한 상담', color: 'text-cyan-400' },
  dream: { icon: 'ri-moon-line', label: '꿈해몽 무제한 상담', color: 'text-purple-400' },
  'four-pillars': { icon: 'ri-ancient-gate-line', label: '사주팔자 무제한 상담', color: 'text-primary-gold' },
  destiny: { icon: 'ri-scales-3-line', label: '운명 결정 비서 무제한', color: 'text-primary-gold' },
};

export default function PayPalSubscriptionModal({
  isOpen,
  onClose,
  chatType = 'astrology',
}: PayPalSubscriptionModalProps) {
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [sdkError, setSdkError] = useState(false);
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const rendered = useRef(false);
  const info = CHAT_TYPE_INFO[chatType] ?? CHAT_TYPE_INFO.astrology;

  useEffect(() => {
    if (!isOpen) {
      setSdkLoaded(false);
      setSubscribed(false);
      setSdkError(false);
      rendered.current = false;
      return;
    }

    // Check if SDK already loaded
    if ((window as any).paypal) {
      setSdkLoaded(true);
      return;
    }

    const existing = document.getElementById('paypal-sdk-script');
    if (existing) {
      setSdkLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.id = 'paypal-sdk-script';
    script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&vault=true&intent=subscription`;
    script.setAttribute('data-sdk-integration-source', 'button-factory');
    script.onload = () => setSdkLoaded(true);
    script.onerror = () => setSdkError(true);
    scriptRef.current = script;
    document.head.appendChild(script);

    return () => {
      // Keep script for future opens
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !sdkLoaded || rendered.current) return;
    const paypal = (window as any).paypal;
    if (!paypal) return;

    const container = document.getElementById(PAYPAL_CONTAINER_ID);
    if (!container) return;
    container.innerHTML = '';
    rendered.current = true;

    try {
      paypal.Buttons({
        style: {
          shape: 'pill',
          color: 'gold',
          layout: 'vertical',
          label: 'subscribe',
        },
        createSubscription: (_data: unknown, actions: any) => {
          return actions.subscription.create({ plan_id: PAYPAL_PLAN_ID });
        },
        onApprove: (_data: any) => {
          setSubscribed(true);
        },
      }).render(`#${PAYPAL_CONTAINER_ID}`);
    } catch (e) {
      setSdkError(true);
    }
  }, [isOpen, sdkLoaded]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={onClose} />

      <div className="relative w-full max-w-sm rounded-3xl overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #0d0820 0%, #0a0618 50%, #0d0820 100%)',
          border: '1px solid rgba(251,191,36,0.4)',
          boxShadow: '0 0 80px rgba(139,92,246,0.3), 0 0 0 1px rgba(255,255,255,0.05) inset',
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-primary-gold to-transparent" />

        <button onClick={onClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white/60 hover:text-white transition-all cursor-pointer"
        >
          <i className="ri-close-line text-base"></i>
        </button>

        {subscribed ? (
          /* ── 구독 성공 ── */
          <div className="p-8 text-center">
            <div className="relative w-20 h-20 mx-auto mb-5">
              <div className="absolute inset-0 rounded-full bg-emerald-400/20 blur-xl animate-pulse" />
              <div className="relative w-full h-full rounded-full bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center">
                <i className="ri-check-line text-3xl text-white"></i>
              </div>
            </div>
            <h3 className="text-2xl font-black text-white mb-2">구독 완료!</h3>
            <p className="text-sm text-purple-300/80 mb-6">이제 무제한으로 AI 상담을 이용하실 수 있습니다.</p>
            <button onClick={onClose}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-400 to-green-500 text-black font-bold hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
            >
              계속 상담하기
            </button>
          </div>
        ) : (
          /* ── 구독 유도 ── */
          <div className="p-6">
            {/* Unlock badge */}
            <div className="flex items-center justify-center mb-4">
              <div className="relative w-16 h-16">
                <div className="absolute inset-0 rounded-2xl bg-primary-gold/20 blur-xl animate-pulse" />
                <div className="relative w-full h-full rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-600 flex items-center justify-center">
                  <i className={`${info.icon} text-2xl text-black`}></i>
                </div>
              </div>
            </div>

            <div className="text-center mb-5">
              <div className="text-[10px] tracking-[0.3em] text-primary-gold/60 uppercase mb-2">무제한 이용권</div>
              <h3 className="text-xl font-black text-white mb-1">{info.label}</h3>
              <p className="text-xs text-purple-300/70">
                1분 이상 대화를 나누셨네요! 구독하시면 토큰 걱정 없이 무제한으로 상담하실 수 있어요.
              </p>
            </div>

            {/* Value props */}
            <div className="space-y-2 mb-5 rounded-2xl p-4"
              style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.18)' }}
            >
              {[
                { icon: 'ri-infinity-line', label: '무제한 AI 상담 메시지' },
                { icon: 'ri-vip-crown-line', label: '사주 + 점성술 + 주역 통합 분석' },
                { icon: 'ri-moon-line', label: '꿈해몽 · 연애운 · 재물운 전체 포함' },
                { icon: 'ri-calendar-check-line', label: '2026 월별 운세 + 대운 풀 리포트' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2.5">
                  <i className={`${item.icon} text-primary-gold text-sm flex-shrink-0`}></i>
                  <span className="text-xs text-white/80">{item.label}</span>
                </div>
              ))}
            </div>

            {/* PayPal button */}
            <div className="mb-4">
              {sdkError ? (
                <div className="text-center py-4 text-xs text-rose-400">
                  PayPal 로드 실패. 잠시 후 다시 시도해주세요.
                </div>
              ) : !sdkLoaded ? (
                <div className="flex items-center justify-center py-5 gap-2">
                  <div className="w-5 h-5 border-2 border-primary-gold/40 border-t-primary-gold rounded-full animate-spin" />
                  <span className="text-xs text-purple-400/60">PayPal 로딩 중...</span>
                </div>
              ) : (
                <div id={PAYPAL_CONTAINER_ID} />
              )}
            </div>

            <p className="text-center text-[10px] text-purple-400/40">
              정기 구독 · 언제든지 취소 가능 · PayPal 보안 결제
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
